"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import styles from "@styles/TaskCard.module.css"
import { TaskSource } from "./TaskSource"
import { Task } from "@database/tasks/saveTasks"
import { Skeleton } from "@mui/material"
import { deserialize } from "@/app/helpers/database/setup/utils"
import { getMessagesFromDb } from "@/app/helpers/actions/getMessagesFromDb"
import { Message } from "@database/messages/saveMessages"
import { Icon, IconName, IconSize } from "../../icons/Icon"
import { Checkbox } from "../../common/checkbox/Checkbox"
import { useTaskContext } from "@/app/context/TaskContext"
import { Badge } from "../../common/badge/Badge"
import { Button, ButtonVariant } from "../../common/button/Button"
import TextArea from "../../common/inputs/TextArea"
import TaskCardHeader from "./TaskCardHeader"
import { StrikethroughText } from "../../common/taskDoneAnimation/StrikethroughText"
import {
  ContextMenuItemType,
  type ContextMenuItemProps,
} from "../../common/contextMenu/ContextMenu"
import clsx from "clsx"

const HOLD_AFTER_STRIKE_MS = 2000

export interface EmailTaskCardContentProps {
  task: Task
  isMaximised?: boolean
  onMaximisedClose?: () => void
}

export default function EmailTaskCardContent({
  task,
  isMaximised = false,
  onMaximisedClose,
}: EmailTaskCardContentProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)

  const { updateTask, deleteTask } = useTaskContext()

  const fetchMessagesFromDb = useCallback(async () => {
    setLoadingMessages(true)
    const result = deserialize<{ messages: Message[]; error: string | null }>(
      await getMessagesFromDb({ threadId: task.threadId }),
    )
    if (result.error) {
      console.error("Error fetching messages from database:", result.error)
      setMessages([])
    } else {
      setMessages(result.messages)
    }
    setLoadingMessages(false)
  }, [task.threadId])

  useEffect(() => {
    fetchMessagesFromDb()
  }, [fetchMessagesFromDb])

  const markTaskAsDone = useCallback(() => {
    updateTask({
      taskId: task.taskId,
      completed: true,
    })
  }, [task.taskId, updateTask])

  const [struck, setStruck] = useState(false)
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // When this card is reused for a different task (e.g. next task takes the slot), reset
  // strike state so the new task is not shown as completed or scheduled for completion.
  useEffect(() => {
    setStruck(false)
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current)
      commitTimeoutRef.current = null
    }
  }, [task.taskId])

  const handleCheckboxChange = useCallback(() => {
    if (task.completed) return
    if (struck) {
      if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current)
      setStruck(false)
    } else {
      setStruck(true)
    }
  }, [task.completed, struck])

  const handleStrikeComplete = useCallback(() => {
    const taskIdToComplete = task.taskId
    const closeModal = isMaximised && onMaximisedClose
    commitTimeoutRef.current = setTimeout(() => {
      updateTask({ taskId: taskIdToComplete, completed: true })
      if (closeModal) onMaximisedClose!()
    }, HOLD_AFTER_STRIKE_MS)
  }, [task.taskId, updateTask, isMaximised, onMaximisedClose])

  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current)
    }
  }, [])

  const title = task.summary
  const description = task.description
  const fullTaskText = [title, description].filter(Boolean).join("\n")

  const senderName = task.source.person

  // Count number of unique participants
  const numberOfParticipants = loadingMessages
    ? null
    : messages.length > 0
      ? new Set(messages.map((message) => message.sender)).size
      : null

  const replyFormId = "email-task-reply-form"

  const handleMaximisedReplySubmit = useCallback(async (text: string) => {
    // TODO: send email reply when implemented
    return ""
  }, [])

  const handlePostpone = useCallback(() => {
    setContextMenuOpen(false)
    updateTask({ taskId: task.taskId, priority: 1 })
    onMaximisedClose?.()
  }, [task.taskId, updateTask, onMaximisedClose])

  const handleDelete = useCallback(async () => {
    setContextMenuOpen(false)
    await deleteTask(task.taskId)
    onMaximisedClose?.()
  }, [task.taskId, deleteTask, onMaximisedClose])

  const maximisedContextMenuItems: ContextMenuItemProps[] = [
    {
      contextMenuItemType: ContextMenuItemType.Option,
      label: "Postpone",
      icon: IconName.ArrowRounded,
      onClick: handlePostpone,
    },
    {
      contextMenuItemType: ContextMenuItemType.Option,
      label: "Delete",
      icon: IconName.TrashCanSimple,
      variant: "destructive",
      onClick: handleDelete,
    },
  ]

  if (isMaximised && onMaximisedClose) {
    return (
      <>
        <TaskCardHeader
          title="Details"
          onClose={onMaximisedClose}
          contextMenuOpen={contextMenuOpen}
          onContextMenuClose={() => setContextMenuOpen(false)}
          contextMenuItems={maximisedContextMenuItems}
          onContextMenuTriggerClick={() => setContextMenuOpen((prev) => !prev)}
          contextMenuPlacement="bottom-left"
        />
        <div className={styles.maximisedContent}>
          <div className={styles.maximisedTaskSection}>
            <div className={styles.maximisedMetaData}>
              <Badge
                label="Gmail"
                size="Medium"
                color="Blue"
                filled={false}
              />
              {senderName != null && (
                <Badge
                  label={senderName}
                  size="Medium"
                  color="Grey"
                  filled={false}
                />
              )}
              {numberOfParticipants != null && numberOfParticipants > 1 && (
                <Badge
                  label={`${numberOfParticipants} Participants`}
                  size="Medium"
                  color="Grey"
                  filled={false}
                />
              )}
            </div>
            {struck ? (
              <StrikethroughText
                text={fullTaskText}
                struck={true}
                onStrikeComplete={handleStrikeComplete}
                textClassName={clsx("bodyText", styles.maximisedDescription)}
              />
            ) : (
              <p className={styles.maximisedDescription}>{description}</p>
            )}
          </div>
          <div className={styles.maximisedReplySection}>
            <TextArea
              formId={replyFormId}
              allowEmptySubmit
              initText=""
              placeholder="Type your answer ..."
              label={`Write answer to ${senderName ?? "sender"}`}
              disabled={false}
              onSubmit={handleMaximisedReplySubmit}
            />
          </div>
        </div>
        <div className={clsx(styles.maximisedFooter, styles.maximisedFooterEmail)}>
          <Button
            type="button"
            label="Mark as done"
            variant={ButtonVariant.Filled}
            disabled={struck}
            onClick={() => setStruck(true)}
          />
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <div
          className={styles.headerCheckbox}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={task.completed || struck}
            onChange={handleCheckboxChange}
            disabled={task.completed}
          />
        </div>
        <TaskSource source={"email"} />
      </div>
      <div className={styles.descriptionContainer}>
        <StrikethroughText
          text={fullTaskText}
          struck={struck}
          onStrikeComplete={handleStrikeComplete}
          textClassName="bodyText"
        />
      </div>
      <div className={clsx("subHeadline", styles.footer)}>
        {senderName ? (
          <span>{senderName}</span>
        ) : (
          <Skeleton width={100} height={20} />
        )}
        {numberOfParticipants ? (
          numberOfParticipants > 1 && (
            <div className={styles.footerGroup}>
              <Icon
                name={IconName.IconGroup1}
                size={IconSize.xSmall}
                customColor="#171717"
              />
              <span>{numberOfParticipants}</span>
            </div>
          )
        ) : (
          <Skeleton width={100} height={20} />
        )}
      </div>
    </>
  )
}
